<template>
  <section class="card-payout-onboarding">
    <div v-if="error" class="workspace-feedback workspace-feedback--error">{{ error }}</div>
    <div ref="mount" class="card-payout-onboarding__mount"></div>
  </section>
</template>

<script>
import { loadConnectAndInitialize } from '@stripe/connect-js'
import { apiService } from '../../common/api.services.js'

export default {
  name: 'CardPayoutOnboarding',
  emits: ['exit', 'financial-status', 'loader-start', 'step-change'],
  props: {
    sessionRequest: { type: Function, default: apiService },
    publishableKey: {
      type: String,
      required: true,
    },
    clientSecret: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      connectInstance: null,
      onboardingElement: null,
      error: '',
      initialClientSecretUsed: false,
    }
  },
  mounted() {
    this.mountOnboarding()
  },
  beforeUnmount() {
    this.clearMount()
  },
  methods: {
    clearMount() {
      if (this.onboardingElement && this.onboardingElement.remove) {
        this.onboardingElement.remove()
      }
      if (this.$refs.mount) {
        this.$refs.mount.innerHTML = ''
      }
      this.onboardingElement = null
      this.connectInstance = null
    },
    resolveErrorMessage(data, fallback) {
      if (!data || typeof data !== 'object') {
        return fallback
      }
      const direct = data.err || data.detail || data.error
      if (typeof direct === 'string' && direct.trim()) {
        return direct
      }
      const firstValue = Object.values(data).find((value) => {
        if (typeof value === 'string') return value.trim()
        return Array.isArray(value) && value.length && typeof value[0] === 'string'
      })
      if (Array.isArray(firstValue)) {
        return firstValue[0]
      }
      return typeof firstValue === 'string' ? firstValue : fallback
    },
    async fetchConnectClientSecret() {
      if (!this.initialClientSecretUsed && this.clientSecret) {
        this.initialClientSecretUsed = true
        return this.clientSecret
      }

      const data = await this.sessionRequest('/api/connect/account-session/', 'POST', { country: this.country || '' }, { timeoutMs: 30000 })
      if (!data || data.err || data.detail || data.error || !data.client_secret) {
        const message = this.resolveErrorMessage(data, 'No pudimos abrir el formulario de cobro.')
        this.error = message
        throw new Error(message)
      }
      if (data.financial_status) {
        this.$emit('financial-status', data.financial_status)
      }
      return data.client_secret
    },
    async mountOnboarding() {
      this.error = ''
      this.initialClientSecretUsed = false
      this.clearMount()

      if (!this.publishableKey || !this.clientSecret) {
        this.error = 'No pudimos abrir el formulario de cobro.'
        return
      }

      try {
        this.connectInstance = loadConnectAndInitialize({
          publishableKey: this.publishableKey,
          fetchClientSecret: () => this.fetchConnectClientSecret(),
          locale: 'es-419',
          appearance: {
            variables: {
              colorPrimary: '#d3dce8',
              colorBackground: '#0b0a12',
              colorText: '#f6fbff',
              colorDanger: '#ff7b9c',
              colorTextSecondary: '#c9d6e6',
              borderRadius: '16px',
              fontFamily: 'Space Grotesk, sans-serif',
            },
          },
        })

        this.onboardingElement = this.connectInstance.create('account-onboarding')
        if (this.onboardingElement.setOnExit) {
          this.onboardingElement.setOnExit(() => this.$emit('exit'))
        }
        if (this.onboardingElement.setOnLoadError) {
          this.onboardingElement.setOnLoadError((loadError) => {
            const stripeError = loadError && loadError.error ? loadError.error : {}
            this.error = stripeError.message || 'Stripe no pudo cargar el formulario de cobro.'
          })
        }
        if (this.onboardingElement.setOnLoaderStart) {
          this.onboardingElement.setOnLoaderStart((event) => {
            this.$emit('loader-start', event)
          })
        }
        if (this.onboardingElement.setOnStepChange) {
          this.onboardingElement.setOnStepChange((stepChange) => {
            this.$emit('step-change', stepChange)
          })
        }
        this.$refs.mount.appendChild(this.onboardingElement)
      } catch (_error) {
        if (!this.error) {
          this.error = 'No pudimos abrir el formulario de cobro. Intenta de nuevo.'
        }
      }
    },
  },
  watch: {
    clientSecret() {
      this.mountOnboarding()
    },
    publishableKey() {
      this.mountOnboarding()
    },
  },
}
</script>

<style scoped>
.card-payout-onboarding {
  display: grid;
  gap: 14px;
  width: 100%;
}

.card-payout-onboarding__mount {
  display: block;
  width: 100%;
  min-height: clamp(620px, 78vh, 920px);
  overflow: visible;
  border-radius: 20px;
  background:
    radial-gradient(circle at 12% 0%, rgba(255, 255, 255, 0.06), transparent 26%),
    linear-gradient(180deg, rgba(8, 8, 14, 0.78), rgba(5, 6, 10, 0.9));
}

.card-payout-onboarding__mount:empty {
  min-height: 240px;
}

.card-payout-onboarding__mount :deep(*) {
  max-width: 100%;
}

.card-payout-onboarding__mount :deep(stripe-connect-account-onboarding),
.card-payout-onboarding__mount :deep(connect-account-onboarding) {
  display: block;
  width: 100%;
  min-height: clamp(620px, 78vh, 920px);
}

.workspace-feedback {
  margin: 0;
}

@media (max-width: 640px) {
  .card-payout-onboarding__mount,
  .card-payout-onboarding__mount :deep(stripe-connect-account-onboarding),
  .card-payout-onboarding__mount :deep(connect-account-onboarding) {
    min-height: 680px;
    border-radius: 18px;
  }
}
</style>
